import React from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { PropsWithRouteConfig, renderRoutes, withRouter } from 'mage-react-router';
import cms from '@moka-fe/cms';
import classNames from 'classnames';
import { Breadcrumb, Button, Modal, NewTabs } from 'sugar-design';
import * as sdf from '@SDFoundation';

import TextLogo from '@/components/common/TextLogo';

import { getApplicationCommentUnread } from '@/redux/actions/comments';
import { addJobFollowed, removeJobFollowed } from '@/redux/actions/jobs';
import { RootState } from '@/redux/reducers';
import { getI18n } from '@/utils/i18n';

import * as styles from './index.cm.styl';

const i18n = getI18n();

enum RouteTab {
  JOBS = 'jobs',
  APPLICATIONS = 'applications',
  EXCEL = 'excel_applications',
}

const mapStateToProps = (state: RootState) => {
  return {
    orgEntities: state.org.entities,
    jobEntities: state.jobs.entities,
    jobIdList: state.jobs.idList,
    applyIdToComment: state.comments.applyIdToComment,
    jobLoading: state.jobs.listLoading,
  };
};

const connector = connect(mapStateToProps, {
  getApplicationCommentUnread,
  addJobFollowed,
  removeJobFollowed,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsWithRouteConfig<PropsFromRedux, { orgId: string }>;

interface State {
  jobFilterId: string | null;
  rpoBatchNo: string | null;
  showDialog: boolean;
  activeTabId?: RouteTab;
}

class Organization extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      jobFilterId: null,
      rpoBatchNo: null, // 筛选条件里的 rpo 批次，这个数据不在页面上展示，但是会存留一段时间，当用户切换筛选之后就会清空
      showDialog: false,
    };
  }

  componentDidMount() {
    const { orgId } = this.props.match.params;
    const activeTabId = Object.values(RouteTab).find(
      (tabId) => this.props.location.pathname.indexOf(`/${tabId}`) >= 0
    );
    if (activeTabId) {
      this.setState({ activeTabId });
    } else {
      this.props.history.push(`/headhunters/${orgId}/org/${RouteTab.JOBS}`);
    }
    this.props.getApplicationCommentUnread(orgId);
  }

  onChangeTab = (tabId: RouteTab) => {
    const {
      params: { orgId },
    } = this.props.match;
    this.setState({ jobFilterId: null, rpoBatchNo: null, activeTabId: tabId });
    this.props.history.push(`/headhunters/${orgId}/org/${tabId}`);
  };

  onRedirectToApplication = (jobId: string, rpoBatchNo: string) => {
    const {
      params: { orgId },
    } = this.props.match;
    this.setState({ jobFilterId: jobId, rpoBatchNo, activeTabId: RouteTab.APPLICATIONS }, () =>
      this.props.history.push(`/headhunters/${orgId}/org/applications`)
    );
  };

  closeDialog = () => this.setState({ showDialog: false });

  openDialog = () => this.setState({ showDialog: true });

  render() {
    const {
      orgEntities,
      jobEntities,
      jobIdList,
      applyIdToComment,
      route,
      match: {
        params: { orgId },
      },
      jobLoading,
    } = this.props;
    const { jobFilterId, rpoBatchNo, showDialog, activeTabId } = this.state;
    let unreadCommentNum = 0;
    Object.keys(applyIdToComment).forEach((key) => {
      if (applyIdToComment[key].unread) {
        unreadCommentNum += 1;
      }
    });
    const currentOrg = orgEntities[orgId] || {};
    const breadcrumbArray = [
      {
        id: '/headhunters/',
        title: i18n.t`首页`,
      },
      {
        id: '/',
        title: currentOrg.name,
      },
    ];
    const announcement = currentOrg.announcement || i18n.t`暂无公告`;

    const routeTabs = [
      { id: 'jobs', title: i18n.t`招聘职位` },
      { id: 'applications', title: i18n.t`推荐记录`, badge: unreadCommentNum },
    ];

    if (Object.values(jobEntities)?.find((item) => item.rpoEnabled)) {
      routeTabs.push({ id: 'excel_applications', title: i18n.t`批量上传历史` });
    }

    return (
      <div className={cms.relative}>
        <Breadcrumb
          className={styles.breadcrumb}
          size="sm"
          options={breadcrumbArray}
          onChange={(path) => this.props.history.push(path)}
        />
        <div className={styles.orgDetailHeaderContent}>
          {currentOrg.logo ? (
            <img className={styles.orgDetailLogo} src={currentOrg.logo} />
          ) : (
            <TextLogo className={styles.orgDetailLogo} text={currentOrg.name} />
          )}
          <div className={styles.orgDetailAnnounceContent}>
            <div className={classNames(sdf.heading30, cms.mgb8)}>{currentOrg.name}</div>
            <div className={sdf.bodyPrimary}>{i18n.t`猎头公告：`}</div>
            <div className={styles.announce}>
              <div dangerouslySetInnerHTML={{ __html: announcement }} />
              {currentOrg.announcement && (
                <Button
                  className={styles.readMore}
                  type="text"
                  onClick={this.openDialog}
                >{i18n.t`阅读全文`}</Button>
              )}
            </div>
          </div>
          <div className={classNames(styles.orgDetailAmount, cms.mgr4)}>
            <span className={classNames(sdf.heading50, cms.mgb4)}>
              {currentOrg.jobNum + currentOrg.jobSpecNum || 0}
            </span>
            <span className={sdf.bodyTertiary}>{i18n.t`招聘职位`}</span>
          </div>
          <div className={styles.orgDetailAmount}>
            <span className={classNames(sdf.heading50, cms.mgb4)}>{currentOrg.applicationNum}</span>
            <span className={sdf.bodyTertiary}>{i18n.t`推荐记录`}</span>
          </div>
        </div>
        <div className={styles.orgSubPageContent}>
          <NewTabs
            size="md"
            noContent
            noBorder
            activeId={activeTabId}
            tabs={routeTabs}
            onChange={this.onChangeTab}
          />
          {!!route?.routes &&
            renderRoutes(route.routes, {
              jobFilterId,
              rpoBatchNo,
              onRedirectToApplication: this.onRedirectToApplication,
              jobEntities,
              jobIdList,
              jobLoading,
              addJobFollowed: this.props.addJobFollowed,
              removeJobFollowed: this.props.removeJobFollowed,
              currentOrg,
            })}
        </div>
        <Modal
          isOpen={showDialog}
          title={i18n.t`猎头公告`}
          onClose={this.closeDialog}
          onConfirm={this.closeDialog}
        >
          <div
            className={styles.announceDialog}
            dangerouslySetInnerHTML={{ __html: announcement }}
          />
        </Modal>
      </div>
    );
  }
}

export default connector(withRouter(Organization));
